import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faHistory, faTimes, faRedo, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getSubscriptionBillings, cancelSubscription, resumeSubscription, updateSubscriptionStatus } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import { AuthContext } from '../contexts/AuthContext';

const Container = styled.div`
  padding: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f5f5f5;
`;

const Title = styled.h2`
  color: #316C6E;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const StatusBadge = styled.span`
  background: #316C6E;
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 0.9em;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  margin: 40px 0;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin-bottom: 25px;
  font-size: 1.4rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #316C6E;
  }
`;

const NextBilling = styled.div`
  background: #f8fafb;
  padding: 30px;
  border-radius: 12px;
  margin: 20px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;

  div {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #4a5568;
    
    svg {
      color: #316C6E;
    }
  }
`;

const WarningBanner = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;
  border: 1px solid #ffeeba;
  display: flex;
  align-items: center;
  gap: 15px;
  font-weight: 500;

  svg {
    font-size: 1.2em;
  }
`;

const BillingList = styled.div`
  display: grid;
  gap: 15px;
`;

const BillingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8fafb;
  border-radius: 12px;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  div:first-child {
    display: grid;
    gap: 5px;
    color: #4a5568;
  }

  div:last-child {
    font-weight: 600;
    color: #316C6E;
    font-size: 1.1em;
  }
`;

const CancelButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  transition: all 0.2s;
  font-size: 1em;

  &:hover {
    background: #c82333;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #e9ecef;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  color: #dc3545;
  margin-bottom: 20px;
`;

const ModalText = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  
  ${props => props.secondary && `
    background-color: #6c757d;
    color: white;
    &:hover {
      background-color: #5a6268;
    }
  `}
  
  ${props => props.danger && `
    background-color: #dc3545;
    color: white;
    &:hover {
      background-color: #c82333;
    }
  `}
`;

const AlertBanner = styled(WarningBanner)`
  background-color: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
`;

const ResumeButton = styled(CancelButton)`
  background: #28a745;
  color: white;
  
  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
  }
`;

const CancelButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SubscriptionManagement = ({ subscriptionId }) => {
  const { user } = useContext(AuthContext);
  const [billings, setBillings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (!subscriptionId) {
      setToastMessage('Subscription ID not found. Please contact support.');
      setToastType('error');
      return;
    }
    loadBillingHistory();
  }, [subscriptionId]);

  const loadBillingHistory = async () => {
    if (!subscriptionId) return;
    
    try {
      setIsLoading(true);
      const response = await getSubscriptionBillings(subscriptionId);
      console.log('Subscription details:', response);
      setBillings(response.billings);
      setSubscriptionDetails({
        ...response.subscription,
        subscription_end_date: user?.subscription_end_date
      });
      console.log('Set subscription details:', {
        ...response.subscription,
        subscription_end_date: user?.subscription_end_date
      });
    } catch (error) {
      console.error('Error loading billing history:', error);
      setToastMessage('Failed to load billing history');
      setToastType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionId) {
      setToastMessage('Subscription ID not found. Please contact support.');
      setToastType('error');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Attempting to cancel subscription:', subscriptionId);
      const response = await cancelSubscription(subscriptionId);
      console.log('Cancellation response:', response);
      
      setToastMessage('Subscription cancelled successfully. You will have access until the end of your current billing period.');
      setToastType('success');
      setShowCancelModal(false);
      
      // Force reload after a short delay
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Detailed cancellation error:', error);
      if (error.response?.status === 404) {
        setToastMessage('Your subscription has already been cancelled or is inactive.');
        setShowCancelModal(false);
        // Force reload after a short delay since the status might have been updated
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setToastMessage('Failed to cancel subscription. Please try again or contact support.');
      }
      setToastType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeUntil = (date) => {
    const now = new Date();
    const end = new Date(date);
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const handleResumeSubscription = async () => {
    if (!subscriptionId) return;

    try {
      setIsLoading(true);
      const response = await resumeSubscription(subscriptionId);
      
      // If this is a new subscription, we need to update the user's subscription ID
      if (response.isNewSubscription) {
        // Update the user's subscription ID in the database
        await updateSubscriptionStatus({
          subscriptionId: response.id,
          status: 'premium'
        });
      }

      setToastMessage('Subscription resumed successfully!');
      setToastType('success');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Error resuming subscription:', error);
      setToastMessage('Failed to resume subscription. Please try again.');
      setToastType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderCancelModal = () => (
    <ConfirmationModal>
      <ModalContent>
        <ModalTitle>Cancel Premium Subscription</ModalTitle>
        <ModalText>
          Are you sure you want to cancel your premium subscription? 
          <br /><br />
          If you cancel:
          <ul>
            <li>Your subscription will remain active until the end of the current billing period</li>
            <li>You'll continue to have access to premium features until then</li>
            <li>No further payments will be processed</li>
            <li>Your account will revert to the free plan after the billing period ends</li>
          </ul>
        </ModalText>
        <ButtonGroup>
          <Button 
            secondary 
            onClick={() => setShowCancelModal(false)}
            disabled={isLoading}
          >
            Keep Subscription
          </Button>
          <Button 
            danger 
            onClick={handleCancelSubscription}
            disabled={isLoading}
          >
            {isLoading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ConfirmationModal>
  );

  if (!subscriptionId) {
    return (
      <Container>
        <Header>
          <Title>Subscription Management</Title>
          <StatusBadge>Premium Active</StatusBadge>
        </Header>
        <div>
          <p>Unable to load subscription details. Please contact support.</p>
          {toastMessage && (
            <Toast
              message={toastMessage}
              type={toastType}
              onClose={() => setToastMessage('')}
            />
          )}
        </div>
      </Container>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const nextBillingDate = subscriptionDetails?.next_billing_date;

  return (
    <Container>
      <Header>
        <Title>Subscription Management</Title>
        <StatusBadge>Premium Active</StatusBadge>
      </Header>

      {subscriptionDetails?.subscription_end_date && (
        <WarningBanner>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Your subscription is scheduled to end on {new Date(subscriptionDetails.subscription_end_date).toLocaleDateString()}.
        </WarningBanner>
      )}

      {subscriptionDetails?.status === 'SUSPENDED' && (
        <AlertBanner>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Your subscription is currently suspended due to a payment failure. 
          Please update your payment method to resume premium access.
        </AlertBanner>
      )}

      <Section>
        <SectionTitle>Current Subscription</SectionTitle>
        <NextBilling>
          <div>
            <FontAwesomeIcon icon={faCreditCard} /> Next billing date:{' '}
            {subscriptionDetails?.next_billing_date ? 
              new Date(subscriptionDetails.next_billing_date).toLocaleDateString() : 
              '-'}
          </div>
          <div>Amount: $4.99 USD</div>
          <div>Status: {subscriptionDetails?.status || 'Active'}</div>
          {subscriptionDetails?.start_time && (
            <div>Started: {new Date(subscriptionDetails.start_time).toLocaleDateString()}</div>
          )}
          {subscriptionDetails?.subscription_end_date && (
            <div>Scheduled End Date: {new Date(subscriptionDetails.subscription_end_date).toLocaleDateString()}</div>
          )}
        </NextBilling>
        <CancelButtonContainer>
          {!subscriptionDetails?.subscription_end_date && (
            <CancelButton onClick={() => setShowCancelModal(true)} disabled={isLoading}>
              <FontAwesomeIcon icon={faTimes} /> Cancel Subscription
            </CancelButton>
          )}
        </CancelButtonContainer>
      </Section>

      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faHistory} /> Billing History
        </SectionTitle>
        <BillingList>
          {billings.length > 0 ? (
            billings.map((billing) => (
              <BillingItem key={billing.id}>
                <div>
                  <div>{new Date(billing.date).toLocaleDateString()}</div>
                  <div>Invoice #{billing.invoice_number}</div>
                  <div>Status: {billing.status}</div>
                </div>
                <div>${billing.amount} USD</div>
              </BillingItem>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              No billing history available yet. Your first charge will appear here soon.
            </div>
          )}
        </BillingList>
      </Section>

      {showCancelModal && renderCancelModal()}
      
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage('')}
        />
      )}
    </Container>
  );
};

export default SubscriptionManagement; 